import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { UserPopover } from '@bonusly/common-user-popover';
import { Avatar, Box, Flex, rem, Skeleton, Text, Tooltip } from '@bonusly/design-system';

import { useLeaderboardPanel } from '../../../../hooks/use-navbar-panels';
import { RecognitionCount } from '../../../bonus-feed/types/user';
import { ProfileLink } from '../../../profile/components/profile-link';
import { LeaderboardDirection } from './types';

/**
 * Skeleton version of the user row for loading states
 * Matches the exact structure of LeaderboardUserRow so there's no layout shift
 */
export const LeaderboardUserRowSkeleton: FC = () => {
	return (
		<Flex as="li" w="100%" alignItems="center" py={2} paddingRight={4} paddingLeft={3} h={rem(68)} borderRadius="lg">
			{/* Rank Skeleton - matches Flex minW={rem(24)} h={rem(24)} */}
			<Flex alignItems="center" justifyContent="center" minW={rem(24)} h={rem(24)}>
				<Skeleton w={rem(20)} h={rem(20)} borderRadius="full" />
			</Flex>

			{/* Gap - matches Box w={rem(12)} */}
			<Box w={rem(12)} flexShrink={0} />

			{/* Avatar Skeleton - wrapped in Box like actual row, size="md" = 40px */}
			<Box flexShrink={0}>
				<Skeleton w="40px" h="40px" borderRadius="full" />
			</Box>

			{/* Gap - matches Box w={rem(12)} */}
			<Box w={rem(12)} flexShrink={0} />

			{/* Name and @username Skeleton - matches Box flex={1} minW={0} */}
			<Box flex={1} minW={0}>
				{/* Display name - matches fontSize="md" lineHeight="24px" */}
				<Skeleton h={rem(20)} w="70%" maxW={rem(160)} mb={rem(4)} borderRadius="sm" />
				{/* Username - matches fontSize="sm" lineHeight="20px" */}
				<Skeleton h={rem(16)} w="50%" maxW={rem(120)} borderRadius="sm" />
			</Box>

			{/* Count Skeleton - right aligned, matches Text flexShrink={0} */}
			<Skeleton h={rem(20)} w={rem(20)} borderRadius="sm" flexShrink={0} />
		</Flex>
	);
};

interface LeaderboardUserRowProps {
	record: RecognitionCount;
	rank: number;
	hashtag?: string | null;
	/** Direction of recognitions - 'received' or 'given'. Affects tooltip text. Defaults to 'received'. */
	direction?: LeaderboardDirection;
}

/**
 * Get rank badge styling based on position
 * Ranks 1-3 get colored circular badges (gold, silver, bronze)
 * Rank 4+ is plain text
 */
const getRankBadgeStyles = (rank: number) => {
	switch (rank) {
		case 1:
			return { bg: 'yellow.50', color: 'yellow.600', hasBadge: true, borderColor: 'bg.surface' };
		case 2:
			return { bg: 'gray.100', color: 'gray.600', hasBadge: true, borderColor: 'bg.surface' };
		case 3:
			return { bg: 'orange.50', color: 'orange.600', hasBadge: true, borderColor: 'bg.surface' };
		default:
			return { bg: 'transparent', color: 'text.soft', hasBadge: false, borderColor: undefined };
	}
};

export const LeaderboardUserRow: FC<LeaderboardUserRowProps> = ({ record, rank, hashtag, direction = 'received' }) => {
	const { t } = useTranslation('navigation');
	const { user, counts } = record;
	const rankStyles = getRankBadgeStyles(rank);
	const { close: closeLeaderboardPanel } = useLeaderboardPanel();

	// Use hashtag-specific count if filtering by hashtag, otherwise use total
	// Strip the # prefix from hashtag since counts keys don't include it
	const hashtagKey = hashtag?.replace(/^#/, '');
	const count = hashtagKey && counts[hashtagKey] !== undefined ? counts[hashtagKey] : counts.total;

	// Format username as @handle
	const username = user.username || '';

	// Use the correct tooltip based on direction
	const tooltipText = direction === 'given' ? t('leaderboard.countTooltipGiven') : t('leaderboard.countTooltip');

	return (
		<ProfileLink userId={user.id} onClick={closeLeaderboardPanel} _hover={{ textDecoration: 'none' }} display="block">
			<Flex
				as="li"
				alignItems="center"
				py={2}
				paddingRight={4}
				paddingLeft={3}
				h={rem(68)}
				borderRadius="lg"
				_hover={{ bg: 'secondary.bgAlpha' }}
				transition="background 0.15s ease"
				cursor="pointer"
			>
				{/* Rank Badge */}
				<Flex
					alignItems="center"
					justifyContent="center"
					minW={rem(24)}
					h={rem(24)}
					borderRadius={rankStyles.hasBadge ? 'full' : undefined}
					bg={rankStyles.bg}
					outline={rankStyles.hasBadge ? '1px solid' : undefined}
					outlineColor={rankStyles.borderColor}
				>
					<Text size="md" variant="medium" color={rankStyles.color} textAlign="center">
						{rank}
					</Text>
				</Flex>

				{/* Gap */}
				<Box w={rem(12)} flexShrink={0} />

				{/* Avatar with UserPopover - stopPropagation prevents panel close, preventDefault prevents navigation */}
				<Box
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
					flexShrink={0}
					w="40px"
					h="40px"
				>
					<UserPopover userId={user.id}>
						<Avatar name={user.display_name} src={user.profile_pic_url} size="md" cursor="pointer" />
					</UserPopover>
				</Box>

				{/* Gap */}
				<Box w={rem(12)} flexShrink={0} />

				{/* Name and @username */}
				<Box flex={1} minW={0}>
					<Text size="md" variant="medium" color="text.default" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" mb={0}>
						{user.display_name}
					</Text>
					<Text size="sm" color="text.softer" mb={0}>
						@{username}
					</Text>
				</Box>

				{/* Count - right aligned with tooltip */}
				<Tooltip label={tooltipText} placement="top">
					<Text size="md" variant="strong" color="text.default" textAlign="right" flexShrink={0}>
						{count}
					</Text>
				</Tooltip>
			</Flex>
		</ProfileLink>
	);
};
